import PostalEntry from 'components/token-town/postal-entry/PostalEntry';
import PurchaseTokensMessage from 'components/token-town/purchase-tokens-message/PurchaseTokensMessage';
import QuantitySelector from 'components/token-town/select-entries/QuantitySelector';
import { getMinigameInfoMessage } from 'utils/common';
import GenericButton from '../../generic-button/GenericButton';
import PageSection from '../../page-section/PageSection';
import sectionStyles from '../../shared/Section.module.css';
import titleStyles from '../../shared/Title.module.css';

import { TTicketDetails } from '@daymade/api-contracts';
import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import { usePurchaseEntries } from 'hooks/token-town/usePurchaseEntries';
import DrawDetails from '../draw-details/DrawDetails';
import LotteryInfo from '../lottery-info/LotteryInfo';
import NumberSelectionBox from '../number-selector/NumberSelectionBox';
import ProgressBarWithBubble from '../progress-bar-with-bubble/ProgressBarWithBubble';
import TotalEntriesCounter from '../total-entries-counter/TotalEntriesCounter';

interface IPurchaseEntriesProps {
  userTokenBalance: number;
  tokenCost: number;
  title: string;
  numberRanges?: TDrawTypeNumberSetup;
  numEntriesPurchased?: number;
  entriesSold: number;
  /** The limit of the number of entries for the draw, or null if there are unlimited entries */
  entryCap: number | null;
  maxPlayerEntries?: number;
  drawSchedule: string | null;
  drawTypeCode: string;
  gameType: string;
  existingTickets: TTicketDetails[];
  handleRedemption: (
    selectedNumbers: number[],
    numberOfEntries: number,
  ) => Promise<void>;
}

const PurchaseEntries = ({
  userTokenBalance,
  title,
  numberRanges,
  entriesSold,
  entryCap,
  tokenCost,
  maxPlayerEntries,
  drawSchedule,
  drawTypeCode,
  gameType,
  existingTickets,
  handleRedemption,
}: IPurchaseEntriesProps) => {
  const {
    numberOfEntries,
    setNumberOfEntries,
    selectedNumbers,
    setSelectedNumbers,
    confirmLoading,
    setConfirmLoading,
    hasEnoughTokens,
    maxEntriesAvailableForPlayer,
    buttonText,
    ctaButtonDisabled,
    entryControlDisabled,
    setInputError,
  } = usePurchaseEntries({
    drawTypeCode,
    gameType,
    existingTickets,
    entryCap,
    entriesSold,
    tokenCost,
    userTokenBalance,
    maxPlayerEntries: maxPlayerEntries ?? null,
  });

  return (
    <PageSection>
      <div
        className={`${sectionStyles.sectionContainer} ${sectionStyles['sectionContainer--spacious']}`}
      >
        <p className={titleStyles.mainTitle}>{title}</p>
        {!entryCap && !numberRanges && (
          <TotalEntriesCounter totalEntries={entriesSold} />
        )}

        {numberRanges && <LotteryInfo numberRanges={numberRanges.main} />}

        <div className={sectionStyles.actionButtons}>
          {!numberRanges && (
            <QuantitySelector
              entryControlDisabled={entryControlDisabled}
              type="entry"
              min={1}
              max={maxEntriesAvailableForPlayer}
              quantity={numberOfEntries}
              setQuantity={setNumberOfEntries}
            />
          )}

          {numberRanges && (
            <NumberSelectionBox
              numberRanges={numberRanges}
              onEditNumbers={(newNumbers, inputError) => {
                setSelectedNumbers(newNumbers.main);
                setInputError(inputError != undefined);
              }}
            />
          )}

          <GenericButton
            onClick={async () => {
              setConfirmLoading(true);
              await handleRedemption(
                selectedNumbers as number[],
                numberOfEntries,
              );
              setConfirmLoading(false);
            }}
            disabled={ctaButtonDisabled}
            loading={confirmLoading}
          >
            {buttonText}
          </GenericButton>
        </div>

        {!hasEnoughTokens ? <PurchaseTokensMessage /> : null}

        <PostalEntry />

        <DrawDetails details={getMinigameInfoMessage(drawSchedule, gameType)} />

        {entryCap != null && (
          <ProgressBarWithBubble
            totalSteps={entryCap}
            currentStep={entriesSold}
          />
        )}
      </div>
    </PageSection>
  );
};
export default PurchaseEntries;
